import { useEffect, useMemo, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { FileBarChart2, Users, Wallet, Clock, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

function initialsOf(name) { return (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join(""); }

function formatCurrencyDays(n) {
  const sign = n > 0 ? "+" : n < 0 ? "" : "";
  return `${sign}${(n ?? 0).toFixed(2)}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/* --------------------- Payroll Report --------------------- */

function PayrollSummaryStrip({ summary }) {
  const items = [
    { label: "Total Working Days", value: summary.total_working_days, icon: CalendarCheck, color: "text-primary",      hint: `${summary.days_in_month}d − ${summary.weekend_days}w − ${summary.holiday_days}h` },
    { label: "Paid Leaves",        value: summary.paid_leaves,        icon: Users,         color: "text-amber-500",   hint: "Approved paid leaves" },
    { label: "Actual Payroll",     value: summary.actual_payroll,     icon: Clock,         color: "text-violet-500",  hint: "Days present (ex-weekend/holiday/leave)" },
    { label: "Total Payroll",      value: summary.total_payroll,      icon: Wallet,        color: "text-emerald-500", hint: `Actual + Paid Leaves ${summary.delta_days >= 0 ? "+" : ""}${summary.delta_days} E/${summary.divisor_used}` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payroll-strip">
      {items.map(({ label, value, icon: Icon, color, hint }) => (
        <div key={label} className="glass rounded-xl p-4 flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center bg-background/50 ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="number font-medium text-xl leading-none mt-1">{value}</div>
            {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function PayrollTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(isAdmin ? "" : user?.id);
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get("/employees").then((r) => {
        setEmployees(r.data);
        if (!employeeId && r.data.length > 0) setEmployeeId(r.data[0].id);
      });
    } else {
      setEmployeeId(user?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.id]);

  useEffect(() => {
    if (!employeeId || !month) return;
    setLoading(true);
    api.get(`/reports/payroll?employee_id=${employeeId}&month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => setLoading(false));
  }, [employeeId, month]);

  const emp = useMemo(() => {
    if (!isAdmin) return { id: user?.id, full_name: user?.full_name, avatar_url: user?.avatar_url };
    return employees.find((e) => e.id === employeeId);
  }, [isAdmin, employees, employeeId, user]);

  return (
    <div className="space-y-5">
      {/* Filters */}
      <SectionCard title="Filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Employee</div>
            {isAdmin ? (
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid="payroll-emp-filter" className="h-10"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}{e.designation ? ` · ${e.designation}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 glass rounded-md px-3 py-2 h-10" data-testid="payroll-emp-locked">
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(user?.avatar_url)} /><AvatarFallback className="text-[9px]">{initialsOf(user?.full_name)}</AvatarFallback></Avatar>
                <span className="text-sm font-medium">{user?.full_name}</span>
                <span className="text-[10px] text-muted-foreground">(locked)</span>
              </div>
            )}
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Month</div>
            <Input data-testid="payroll-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" />
          </div>
          {emp && (
            <div className="ml-auto flex items-center gap-2 text-sm">
              <Avatar className="h-9 w-9"><AvatarImage src={authedUrl(emp.avatar_url)} /><AvatarFallback>{initialsOf(emp.full_name)}</AvatarFallback></Avatar>
              <div>
                <div className="font-medium">{emp.full_name}</div>
                <div className="text-[11px] text-muted-foreground">{emp.designation || "—"} · {emp.department || "—"}</div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {loading && <div className="text-sm text-muted-foreground">Loading payroll…</div>}

      {data && !loading && (
        <>
          <PayrollSummaryStrip summary={data.summary} />

          <SectionCard title={`Daily Breakdown · ${data.month}`} subtitle={`${data.summary.days_in_month} days · ${data.summary.weekend_days} weekends · ${data.summary.holiday_days} holiday${data.summary.holiday_days === 1 ? "" : "s"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-table">
                <thead>
                  <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2 pr-3">Date</th>
                    <th className="text-left py-2 pr-3">Day</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Clock In</th>
                    <th className="text-left py-2 pr-3">Clock Out</th>
                    <th className="text-right py-2 pr-3">Active Hours</th>
                    <th className="text-right py-2 pr-3">Idle Hours</th>
                    <th className="text-right py-2 pr-3">Extra / Less</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const att = r.attendance;
                    const bgClass = r.is_holiday
                      ? "bg-amber-500/5"
                      : r.is_weekend
                      ? "bg-muted/30"
                      : r.leave
                      ? "bg-primary/5"
                      : "";
                    let statusLabel = "—";
                    let statusTone = "outline";
                    const workedOffday = !!(att && (r.is_holiday || r.is_weekend || r.leave));
                    if (r.is_holiday) { statusLabel = `Holiday · ${r.holiday_title}`; statusTone = "secondary"; }
                    else if (r.is_weekend) { statusLabel = "Weekend"; statusTone = "secondary"; }
                    else if (r.leave) { statusLabel = `Leave (${r.leave.leave_type}${r.leave.is_paid ? " · Paid" : " · Unpaid"})`; statusTone = r.leave.is_paid ? "secondary" : "outline"; }
                    else if (att) { statusLabel = att.mode_display || att.status || "—"; }
                    else { statusLabel = "No record"; }
                    return (
                      <tr key={r.date} className={`border-b border-border/30 ${bgClass}`}>
                        <td className="py-2 pr-3 number">{r.date}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={r.is_weekend ? "secondary" : "outline"} className="text-[10px]">{r.weekday}</Badge>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge variant={statusTone} className="text-[10px] max-w-[200px] truncate inline-block align-middle">{statusLabel}</Badge>
                            {workedOffday && (
                              <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px]" title="Worked on an off day — hours counted as Extra under WFH">
                                WFH · bonus
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3">{att?.check_in ? new Date(att.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="py-2 pr-3">{att?.check_out ? new Date(att.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                        <td className="py-2 pr-3 text-right number">{att?.active_hours != null ? att.active_hours.toFixed(2) : "—"}</td>
                        <td className="py-2 pr-3 text-right number text-muted-foreground">{att?.idle_hours != null ? att.idle_hours.toFixed(2) : "—"}</td>
                        <td className={`py-2 pr-3 text-right number ${r.extra_less_hours > 0 ? "text-emerald-500" : r.extra_less_hours < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                          {r.extra_less_display}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-medium" data-testid="payroll-totals">
                    <td className="py-3 pr-3" colSpan={6}></td>
                    <td className="py-3 pr-3 text-right text-[11px] uppercase tracking-wider text-muted-foreground">Total Extra / Less</td>
                    <td className={`py-3 pr-3 text-right number ${data.summary.extra_less_hours_total > 0 ? "text-emerald-500" : data.summary.extra_less_hours_total < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                      {data.summary.extra_less_display}
                      <div className="text-[10px] text-muted-foreground mt-0.5 font-normal">= {formatCurrencyDays(data.summary.delta_days)} day{Math.abs(data.summary.delta_days) === 1 ? "" : "s"} (÷{data.summary.divisor_used}h)</div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

/* --------------------- Reports page --------------------- */

const TABS = [
  { value: "payroll",    label: "Employee Payroll Report" },
  { value: "performance", label: "Employee Performance Report" },
];

export default function Reports() {
  const [tab, setTab] = useState("payroll");

  return (
    <div className="space-y-6">
      <PageHeader testid="reports-header" title="Reports" subtitle="Executive & operational insights" />
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="reports-tabs" className="flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="payroll" className="mt-4">
          <PayrollTab />
        </TabsContent>
        <TabsContent value="performance" className="mt-4">
          <SectionCard title="Employee Performance Report" subtitle="Coming soon">
            <div className="text-sm text-muted-foreground py-8 text-center">
              This report is on the roadmap. For now, see the Payroll tab.
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
